<script setup>
/**
 * SzAccountSwitcher (legado: SzCollapsedAccountSwitcher) — seletor de conta
 * unificado para sidebar colapsada e expandida.
 *
 * Props:
 *   mode: 'collapsed' (default — avatar 28px circular)
 *         'expanded'  (pill horizontal com nome + chevron)
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  mode: {
    type: String,
    default: 'collapsed',
    validator: v => ['collapsed', 'expanded'].includes(v),
  },
});

const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const userAccounts = useMapGetter('getUserAccounts');

const showDropdown = ref(false);
const btnRef = ref(null);
const dropdownPos = ref({ top: 0, left: 0 });

const sortedAccounts = computed(() =>
  [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);

function deriveInitials(name) {
  if (!name) return '';
  const stripped = name.trim().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const words = stripped.split(/\s+/).filter(w => /[a-z0-9]/i.test(w));
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return stripped.slice(0, 2).toUpperCase();
}
const accountInitials = computed(() => deriveInitials(currentAccount.value?.name));

const hasMultipleAccounts = computed(() => userAccounts.value.length > 1);

const ROLE_LABEL = {
  administrator: 'Administrador',
  agent: 'Agente',
};
function roleLabel(role) {
  return ROLE_LABEL[role] || role || '';
}

function toggle() {
  if (!hasMultipleAccounts.value) return;
  if (!showDropdown.value) updatePosition();
  showDropdown.value = !showDropdown.value;
}
function close() {
  showDropdown.value = false;
}
function updatePosition() {
  if (!btnRef.value) return;
  const rect = btnRef.value.getBoundingClientRect();
  if (props.mode === 'expanded') {
    dropdownPos.value = { top: rect.bottom + 8, left: rect.left };
  } else {
    dropdownPos.value = { top: rect.top, left: rect.right + 8 };
  }
}
function onChangeAccount(id) {
  if (id === accountId.value) {
    close();
    return;
  }
  window.location.href = `/app/accounts/${id}/dashboard`;
}
function onClickOutside(e) {
  if (btnRef.value && btnRef.value.contains(e.target)) return;
  close();
}
onMounted(() => {
  document.addEventListener('click', onClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside);
});
</script>

<template>
  <button
    v-if="mode === 'collapsed'"
    ref="btnRef"
    class="sz-acct-btn sz-acct-btn--collapsed"
    :title="currentAccount.name"
    @click.stop="toggle"
  >
    <span class="sz-acct-avatar">{{ accountInitials }}</span>
  </button>
  <button
    v-else
    ref="btnRef"
    class="sz-acct-btn sz-acct-btn--expanded"
    :class="{ open: showDropdown }"
    :title="currentAccount.name"
    @click.stop="toggle"
  >
    <span class="sz-acct-avatar sz-acct-avatar--sm">{{ accountInitials }}</span>
    <span class="sz-acct-name">{{ currentAccount.name }}</span>
    <svg
      v-if="hasMultipleAccounts"
      class="sz-acct-chevron"
      :class="{ open: showDropdown }"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </button>

  <Teleport to="body">
    <div
      v-if="showDropdown"
      class="sz-acct-dropdown"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
    >
      <div class="sz-acct-dropdown-title">Altera conta</div>
      <div class="sz-acct-dropdown-list">
        <button
          v-for="account in sortedAccounts"
          :key="account.id"
          type="button"
          class="sz-acct-item"
          :class="{ 'sz-acct-item--active': account.id === accountId }"
          @click="onChangeAccount(account.id)"
        >
          <div class="sz-acct-item-text">
            <span class="sz-acct-item-role">{{ roleLabel(account.role) }}</span>
            <span class="sz-acct-item-name">{{ account.name }}</span>
          </div>
          <svg
            v-if="account.id === accountId"
            class="sz-acct-item-check"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
      <!-- Overlay de fade no rodapé (pointer-events:none, não bloqueia scroll) -->
      <div class="sz-acct-dropdown-fade" aria-hidden="true" />
    </div>
  </Teleport>
</template>

<style scoped>
/* ── Triggers ───────────────────────────────────────────── */
.sz-acct-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Inter', system-ui, sans-serif;
  transition: background 0.15s;
}
.sz-acct-btn--collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  border-radius: 8px;
}
.sz-acct-btn--collapsed:hover { background: rgba(0,0,0,0.05); }

.sz-acct-btn--expanded {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 6px 8px;
  border-radius: 8px;
  color: #000000;
  font-size: 14px;
  font-weight: 500;
}
.sz-acct-btn--expanded:hover { background: rgba(0,0,0,0.04); }
.sz-acct-btn--expanded.open { background: rgba(0,0,0,0.05); }

.sz-acct-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #16A34A;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sz-acct-avatar--sm { width: 24px; height: 24px; font-size: 10px; }
.sz-acct-name {
  flex: 1;
  min-width: 0;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-acct-chevron {
  color: #6B7280;
  flex-shrink: 0;
  transition: transform 0.15s;
}
.sz-acct-chevron.open { transform: rotate(180deg); }
</style>

<style>
/* Dropdown — Teleport to body. NOT scoped. */
.sz-acct-dropdown {
  position: fixed;
  width: 278px;
  max-height: min(420px, 80vh);
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  box-shadow: 0px 8px 32px rgba(15, 23, 42, 0.1);
  border-radius: 8px;
  padding: 0 0 4px;
  font-family: 'Inter', system-ui, sans-serif;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sz-acct-dropdown-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 41px;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 100%);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
.sz-acct-dropdown-title {
  padding: 8px 16px;
  height: 36px;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #828282;
  flex-shrink: 0;
}
.sz-acct-dropdown-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 28px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sz-acct-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 4px 8px;
  border: none;
  background: #FFFFFF;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
  text-align: left;
}
.sz-acct-item:hover { background: #F1F1F1; }
.sz-acct-item--active { background: #E4EFE4; }
.sz-acct-item--active:hover { background: #DAE8DA; }
.sz-acct-item-text {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  min-width: 0;
}
.sz-acct-item-role {
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #525252;
}
.sz-acct-item-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-acct-item-check {
  color: #388E3C;
  flex-shrink: 0;
}
.sz-acct-dropdown-list::-webkit-scrollbar { width: 8px; }
.sz-acct-dropdown-list::-webkit-scrollbar-track { background: transparent; }
.sz-acct-dropdown-list::-webkit-scrollbar-thumb { background: #D4D4D4; border-radius: 4px; }

/* ── Dark mode ──────────────────────────────────────────── */
.dark .sz-acct-btn--collapsed:hover { background: rgba(255,255,255,0.06); }
.dark .sz-acct-btn--expanded { color: #E5E7EB; }
.dark .sz-acct-btn--expanded:hover { background: rgba(255,255,255,0.05); }
.dark .sz-acct-btn--expanded.open { background: rgba(255,255,255,0.07); }
.dark .sz-acct-chevron { color: #9CA3AF; }

.dark .sz-acct-dropdown {
  background: #1F2125;
  border-color: #2A2C30;
  box-shadow: 0px 8px 32px rgba(0,0,0,0.5);
}
.dark .sz-acct-dropdown-title { color: #9797A0; }
.dark .sz-acct-dropdown-fade {
  background: linear-gradient(180deg, rgba(31,33,37,0) 0%, #1F2125 100%);
}
.dark .sz-acct-item { background: transparent; }
.dark .sz-acct-item:hover { background: rgba(255,255,255,0.05); }
.dark .sz-acct-item--active { background: rgba(56,142,60,0.18); }
.dark .sz-acct-item--active:hover { background: rgba(56,142,60,0.25); }
.dark .sz-acct-item-role { color: #9797A0; }
.dark .sz-acct-item-name { color: #E5E7EB; }
.dark .sz-acct-item-check { color: #8ACE8A; }
.dark .sz-acct-dropdown-list::-webkit-scrollbar-thumb { background: #3D3F43; }
</style>
